/**
 * BulkEditControls Component
 * 
 * Provides bulk edit functionality for annotations including:
 * - Select/Deselect all
 * - Bulk approve (mark as reviewed)
 * - Bulk delete
 * - Progress tracking
 * - Exit bulk mode
 */
import React, { memo } from 'react';
import {
    View,
    Alert,
    Button,
    IconButton,
    IconXSolid,
    IconCertifiedLine,
    IconTrashLine,
    Flex,
} from '@smarterservices/smarter-ui';
import ButtonWithConfirmation from '@ui/shared/components/Common/ButtonWithConfirmation';
import { NavigationalToolTip } from '@ui/shared/components/Common/NavigationalToolTip';
import CONSTANTS from '../../../config/constants';

const BulkEditControls = memo(({
    bulkEdit,
    bulkChangeInProcess,
    bulkChangePercentage,
    bulkChangeType,
    bulkChangeCompleted,
    bulkSelectedItems,
    totalAnnotations,
    onSelectAll,
    onDeselectAll,
    onBulkApprove,
    onBulkDelete,
    onExitBulkMode,
}) => {
    if (!bulkEdit) return null;

    return (
        <>
            {bulkChangeInProcess && (
                <Alert size="small">
                    {bulkChangePercentage}% of the annotations have been {bulkChangeType === "approve" ? "approved" : "deleted"}.
                </Alert>
            )}

            {bulkChangeCompleted && (
                <Alert variant="success" size="small" timeout={5000}>
                    All selected annotations have been {bulkChangeType === "approve" ? "set as reviewed" : "deleted"}.
                </Alert>
            )}

            <View
                as="div"
                className="bulk-edit-options"
                background="secondary"
                borderColor="primary"
                borderRadius="medium"
                borderWidth="small"
                padding="x-small"
                margin="none none xx-small none"
            >
                <Flex justifyItems="space-between">
                    {!bulkChangeInProcess && (
                        <Flex.Item>
                            {bulkSelectedItems.length === totalAnnotations ? (
                                <Button size="small" onClick={onDeselectAll}>
                                    Deselect All
                                </Button>
                            ) : (
                                <Button size="small" onClick={onSelectAll}>
                                    Select All
                                </Button>
                            )}

                            {bulkSelectedItems.length > 0 && (
                                <>
                                    <ButtonWithConfirmation
                                        margin="none none none x-small"
                                        onConfirm={onBulkApprove}
                                        confirmationText={`Mark ${bulkSelectedItems.length} item${bulkSelectedItems.length > 1 ? 's' : ''} as reviewed?`}
                                        confirmButtonText="Yes"
                                        confirmButtonColor="success"
                                        color="success"
                                        screenReaderLabel={`Mark ${bulkSelectedItems.length} item${bulkSelectedItems.length > 1 ? 's' : ''} as reviewed?`}
                                    >
                                        <IconCertifiedLine />
                                    </ButtonWithConfirmation>
                                    <ButtonWithConfirmation
                                        margin="none none none x-small"
                                        onConfirm={onBulkDelete}
                                        confirmationText={`Delete ${bulkSelectedItems.length} selected item${bulkSelectedItems.length > 1 ? 's' : ''}?`}
                                        confirmButtonText="Yes"
                                        confirmButtonColor="danger"
                                        color="danger"
                                        screenReaderLabel={`Delete ${bulkSelectedItems.length} selected item${bulkSelectedItems.length > 1 ? 's' : ''}?`}
                                    >
                                        <IconTrashLine />
                                    </ButtonWithConfirmation>
                                </>
                            )}
                        </Flex.Item>
                    )}
                    <Flex.Item alignItems="end">
                        <NavigationalToolTip
                            description="Close bulk edit"
                            shortcut={CONSTANTS?.KEYBOARD_SHORTCUTS?.ANNOTATIONS?.OPTIONS?.BULK_EDIT?.KEY}
                        >
                            <IconButton
                                size="small"
                                screenReaderLabel="Exit Bulk Edit Mode"
                                onClick={onExitBulkMode}
                            >
                                <IconXSolid />
                            </IconButton>
                        </NavigationalToolTip>
                    </Flex.Item>
                </Flex>
            </View>
        </>
    );
});

BulkEditControls.displayName = 'BulkEditControls';

export default BulkEditControls;
